import {PayloadAction, createSlice} from '@reduxjs/toolkit';
import {DateTime} from 'luxon';

import {OFFERS_GROUPING} from 'components/Offers/OfferGroup';
import {
  OCRMReward,
  OCRMStatus,
  Offer,
  OfferSource,
  OfferType,
  Offers,
  OffersHeaders,
  OffersResponse,
  RewardState,
  SaveOfferPrefResponse,
  TDSLink,
  TDSOffer,
  TDSReward,
} from 'models/Offer';
import {
  OfferFiltersDestination,
  OfferFiltersProperty,
} from 'models/OfferFilters';
import {ReservationDate} from 'models/Reservations';
import {GetOffersOpts, SaveOfferPrefOpts} from 'services/api/offers/offers';
import {AuthActions} from 'store/auth/auth.reducer';

export enum OFFER_TAB {
  AVAILABLE = 'AVAILABLE',
  FAVORITES = 'FAVORITES',
}

export enum OFFERS_LOADING_STATUS {
  CANCELED = 'CANCELED',
  FETCHING = 'FETCHING',
  SUCCESS = 'SUCCESS',
  FAILED = 'FAILED',
}

export enum OCRM_ACCEPT_STATUS {
  DEFAULT = 'DEFAULT',
  SUCCESS = 'SUCCESS',
  FAILED = 'FAILED',
}

export interface SelectedFilters {
  readonly reservationConfirmationCode?: string;
  readonly startDate?: string;
  readonly endDate?: string;
  readonly destination?: OfferFiltersDestination;
  readonly properties?: OfferFiltersProperty[];
  readonly type?: OfferType[];
}

export interface AvailableFilters {
  readonly startDate?: string;
  readonly endDate?: string;
  readonly destination?: OfferFiltersDestination[];
  readonly properties?: string[];
  readonly types: OfferType[];
}

export interface OCRMOfferAcceptTypes {
  readonly loading: boolean;
  readonly status: OCRM_ACCEPT_STATUS;
  readonly error: string | undefined;
}

export interface OffersState {
  readonly lastQueryTimes: {
    ocrm?: string; //ISO Date
    all?: string;
  };
  readonly header?: OffersHeaders;
  readonly offers: Offers;
  readonly loading: boolean;
  readonly failed: boolean;
  readonly status: OFFERS_LOADING_STATUS;
  readonly selectedFilters: SelectedFilters;
  readonly selectedFavoritesFilters: SelectedFilters;
  readonly savePrefFailed: boolean;
  readonly selectedOfferId: string | undefined;
  readonly seeMoreOffers?: boolean;
  readonly OCRMOfferAccept: OCRMOfferAcceptTypes;
  readonly saveOfferPrefLoading: string[];
  readonly currentOfferTab: string;
  readonly newOffers: Offer[];
  readonly hasNewOffers: boolean;
  readonly isDomMarketBannerVisible: boolean;
  readonly focusedCardId: string | undefined;
  readonly selectedGrouping: {key: OFFERS_GROUPING; name: string} | undefined;
  readonly isListView: boolean;
  readonly offersRefreshFailCount: number;
}

export enum RefreshOffersAction {
  refresh = 'refresh',
  dismiss = 'dismiss',
}

export const initialSelectedFilters = {
  reservationConfirmationCode: undefined,
  startDate: undefined,
  endDate: undefined,
  destination: undefined,
  properties: undefined,
  type: undefined,
};

export const initialOCRMOfferAccept = {
  loading: false,
  status: OCRM_ACCEPT_STATUS.DEFAULT,
  error: undefined,
};

export const initialState: OffersState = {
  lastQueryTimes: {
    ocrm: undefined,
    all: undefined,
  },
  status: OFFERS_LOADING_STATUS.CANCELED,
  header: undefined,
  offers: {},
  loading: false,
  failed: false,
  selectedFilters: initialSelectedFilters,
  selectedFavoritesFilters: initialSelectedFilters,
  savePrefFailed: false,
  selectedOfferId: undefined,
  seeMoreOffers: false,
  OCRMOfferAccept: initialOCRMOfferAccept,
  saveOfferPrefLoading: [],
  newOffers: [],
  hasNewOffers: false,
  currentOfferTab: OFFER_TAB.AVAILABLE,
  isDomMarketBannerVisible: false,
  focusedCardId: undefined,
  selectedGrouping: undefined,
  isListView: false,
  offersRefreshFailCount: 0,
};

const keyOffersToObj = (offers: Offers, currentOffer: Offer) => {
  if (!currentOffer.end) {
    currentOffer.end = DateTime.local().endOf('year').toISO();
  }
  const endDateTime = DateTime.fromISO(currentOffer.end).toLocal();
  const startDateTime = DateTime.fromISO(currentOffer.start).toLocal();
  const now = DateTime.local();

  if (endDateTime > now && endDateTime.isValid && startDateTime.isValid) {
    return {
      ...offers,
      [currentOffer.id]: currentOffer, // Over-write any offers with this id
    };
  }
  return offers;
};

/**
 * https://redux-toolkit.js.org/api/createSlice
 * Because reducers uses createReducer internally
 * we can safely "mutate" state writing reducers
 */
const offerSlice = createSlice({
  name: 'offers',
  initialState,
  reducers: {
    clearOffers() {
      return initialState;
    },
    clearFilters(state) {
      if (state.currentOfferTab === OFFER_TAB.AVAILABLE) {
        state.selectedFilters = initialSelectedFilters;
        if (state.isDomMarketBannerVisible) {
          state.isDomMarketBannerVisible = false;
        }
      } else {
        state.selectedFavoritesFilters = initialSelectedFilters;
      }
    },
    getOffers(state, _action: PayloadAction<GetOffersOpts>) {
      state.status = OFFERS_LOADING_STATUS.FETCHING;
      state.loading = true;
      state.failed = false;
    },
    pauseOffers() {},
    refreshOcrmOffers() {},
    saveOffers(state, action: PayloadAction<OffersResponse>) {
      state.status = OFFERS_LOADING_STATUS.SUCCESS;
      state.loading = false;
      state.failed = false;
      const now = DateTime.local().toISO();
      state.lastQueryTimes.ocrm = now;
      state.lastQueryTimes.all = now;
      state.header = action.payload.header;
      state.offersRefreshFailCount = 0;
      const hasNewOffers = action.payload.offers.some(
        offer =>
          state.offers[offer.id] === undefined ||
          state.offers[offer.id].title !== offer.title
      );
      if (hasNewOffers) {
        state.newOffers = action.payload.offers;
        state.hasNewOffers = true;
      }
    },
    refreshOffers(state, action: PayloadAction<RefreshOffersAction>) {
      state.hasNewOffers = false;
      if (action.payload === RefreshOffersAction.refresh) {
        state.offers = state.newOffers.reduce(keyOffersToObj, {});
      }
    },
    removeExpiredOffers(state, action: PayloadAction<Offer[]>) {
      state.offers = action.payload.reduce(keyOffersToObj, {});
    },
    getOffersSuccess(state, action: PayloadAction<OffersResponse>) {
      state.status = OFFERS_LOADING_STATUS.SUCCESS;
      state.loading = false;
      state.failed = false;
      const now = DateTime.local().toISO();
      const source = action?.payload?.source;
      const isAll = source ? source === OfferSource.all : true;
      state.lastQueryTimes.ocrm = now;
      let filteredOffers = {};
      if (isAll) {
        state.lastQueryTimes.all = now;
      } else {
        const oldOffers = Object.values(state.offers);
        filteredOffers = oldOffers
          .filter(o => o.source !== source) // Remove source type we fetched
          .reduce(keyOffersToObj, {}); // Re-map back to offers obj.
      }
      state.offers = action.payload.offers
        ? action.payload.offers.reduce(keyOffersToObj, filteredOffers)
        : {};
      state.header = action.payload.header;
      state.offersRefreshFailCount = 0;
    },
    getOffersFailure(state, action: PayloadAction<OffersResponse>) {
      state.lastQueryTimes = initialState.lastQueryTimes;
      state.status = OFFERS_LOADING_STATUS.FAILED;
      state.loading = false;
      state.failed = true;
      state.header = action.payload?.header;
      state.offersRefreshFailCount += 1;
    },
    setSelectedDestinationFilter(
      state,
      action: PayloadAction<OfferFiltersDestination | undefined>
    ) {
      if (state.currentOfferTab === OFFER_TAB.AVAILABLE) {
        state.selectedFilters = {
          ...state.selectedFilters,
          destination: action.payload,
          properties: [],
        };
        if (state.isDomMarketBannerVisible) {
          state.isDomMarketBannerVisible = false;
        }
      } else {
        state.selectedFavoritesFilters = {
          ...state.selectedFavoritesFilters,
          destination: action.payload,
          properties: [],
        };
      }
    },
    setSelectedPropertyFilter(
      state,
      action: PayloadAction<OfferFiltersProperty[] | undefined>
    ) {
      if (state.currentOfferTab === OFFER_TAB.AVAILABLE) {
        state.selectedFilters = {
          ...state.selectedFilters,
          properties: action.payload,
        };
      } else {
        state.selectedFavoritesFilters = {
          ...state.selectedFavoritesFilters,
          properties: action.payload,
        };
      }
    },
    saveOfferPref(state, action: PayloadAction<SaveOfferPrefOpts>) {
      if (!state.offers || !state.offers[action.payload.offerid]) {
        return;
      }

      state.offers[action.payload.offerid].pref = action.payload.preference;
    },

    saveOfferPrefStatus(state, action: PayloadAction<string>) {
      if (!state.saveOfferPrefLoading.includes(action.payload)) {
        state.saveOfferPrefLoading = [
          ...state.saveOfferPrefLoading,
          action.payload,
        ];
      } else {
        state.saveOfferPrefLoading = state.saveOfferPrefLoading.filter(
          offerId => offerId !== action.payload
        );
      }
    },

    saveOfferPrefFailure(state, action: PayloadAction<SaveOfferPrefResponse>) {
      state.savePrefFailed = true;
      state.header = action.payload.header;
    },
    setSelectedOfferId(state, action: PayloadAction<string | undefined>) {
      state.selectedOfferId = action.payload;
    },
    setStartDate(state, action: PayloadAction<string | undefined>) {
      if (state.currentOfferTab === OFFER_TAB.AVAILABLE) {
        state.selectedFilters.startDate = action.payload;
        state.selectedFilters.reservationConfirmationCode = undefined;
      } else {
        state.selectedFavoritesFilters.startDate = action.payload;
        state.selectedFavoritesFilters.reservationConfirmationCode = undefined;
      }
    },
    setEndDate(state, action: PayloadAction<string | undefined>) {
      if (state.currentOfferTab === OFFER_TAB.AVAILABLE) {
        state.selectedFilters.endDate = action.payload;
        state.selectedFilters.reservationConfirmationCode = undefined;
      } else {
        state.selectedFavoritesFilters.endDate = action.payload;
        state.selectedFavoritesFilters.reservationConfirmationCode = undefined;
      }
    },
    reservationSelected(state, {payload}: PayloadAction<ReservationDate>) {
      // Check if already selected, if so clear the confirmation code and filter dates.
      const helper =
        state.currentOfferTab === OFFER_TAB.AVAILABLE
          ? state.selectedFilters
          : state.selectedFavoritesFilters;

      const alreadySelected =
        payload.confirmationCode === helper.reservationConfirmationCode;

      helper.reservationConfirmationCode = alreadySelected
        ? undefined
        : payload.confirmationCode;

      helper.startDate = alreadySelected
        ? undefined
        : DateTime.fromFormat(payload.checkInDate, 'MM-dd-yyyy', {
            zone: 'America/Chicago',
          }).toFormat('yyyy-MM-dd');

      helper.endDate = alreadySelected
        ? undefined
        : DateTime.fromFormat(payload.checkOutDate, 'MM-dd-yyyy', {
            zone: 'America/Chicago',
          }).toFormat('yyyy-MM-dd');
    },
    setSelectedOfferTypeFilter(state, action: PayloadAction<OfferType[]>) {
      const helper =
        state.currentOfferTab === OFFER_TAB.AVAILABLE
          ? state.selectedFilters
          : state.selectedFavoritesFilters;
      helper.type = action.payload;
    },
    setSelectedFilters(state, action: PayloadAction<SelectedFilters>) {
      if (state.currentOfferTab === OFFER_TAB.AVAILABLE) {
        state.selectedFilters = action.payload;
      } else {
        state.selectedFavoritesFilters = action.payload;
      }
    },
    switchIsListView(state) {
      state.isListView = !state.isListView;
    },
    bookOfferCalendar(_state, _action: PayloadAction<string | undefined>) {},
    acceptOCRMOffer(state, _action: PayloadAction<OCRMReward>) {
      state.OCRMOfferAccept.loading = true;
    },
    acceptOCRMOfferSuccess(state, action: PayloadAction<TDSOffer>) {
      if (state.offers) {
        state.OCRMOfferAccept.loading = false;
        state.OCRMOfferAccept.status = OCRM_ACCEPT_STATUS.SUCCESS;

        const {qualifierId, linkList, rewardList} = action.payload;
        const tdsState = action.payload.state;
        const currentOffers = {...state.offers[`${qualifierId}`]};
        const currentOffer = {...currentOffers};

        if (currentOffer.source !== OfferSource.ocrm) {
          return;
        }

        if (tdsState === 'DELIVERED') {
          currentOffer.status = OCRMStatus.ACCEPTED;
        } else if (tdsState === 'DELIVERING') {
          currentOffer.status = OCRMStatus.ACTIVE;
        } else {
          currentOffer.status = tdsState.replace(/^\w/, (char: string) =>
            char.toUpperCase()
          );
        }

        currentOffer.ocrm.links.link = linkList.link.map((link: TDSLink) => ({
          href: link.href,
          rel: link.rel,
        }));

        currentOffer.ocrm.rewards = currentOffer.ocrm.rewards.map(
          (reward: OCRMReward) => {
            const filteredReward: TDSReward[] = rewardList.reward.filter(
              (tdsReward: TDSReward) => {
                return tdsReward.rewardId === reward.id;
              }
            );

            if (filteredReward.length) {
              if (filteredReward[0].rewardState === RewardState.SUCCESS) {
                reward.state = RewardState.SUCCESS;
              } else if (
                filteredReward[0].rewardState === RewardState.PENDING
              ) {
                reward.state = RewardState.PENDING;
              } else {
                reward.state = RewardState.ERROR;
              }
            }

            return reward;
          }
        );

        state.offers[`${qualifierId}`] = currentOffer;
      }
    },
    acceptOCRMOfferFailure(state, action: PayloadAction<string | undefined>) {
      state.OCRMOfferAccept.loading = false;
      state.OCRMOfferAccept.status = OCRM_ACCEPT_STATUS.FAILED;
      state.OCRMOfferAccept.error = action.payload;
    },
    clearAcceptOCRMOfferState(state) {
      state.OCRMOfferAccept = initialOCRMOfferAccept;
    },
    acceptOfferSuccess(_state, _action: PayloadAction<string | undefined>) {},
    checkDefaultOfferTab() {},
    setActiveOfferTab(state, action: PayloadAction<string>) {
      state.currentOfferTab = action.payload;
    },
    showDomMarketBanner(state, action: PayloadAction<boolean>) {
      state.isDomMarketBannerVisible = action.payload;
    },
    setFocusedCardId(state, action: PayloadAction<string | undefined>) {
      state.focusedCardId = action.payload;
    },
    setDomMarket() {},
    setSelectedGrouping(
      state,
      action: PayloadAction<{key: OFFERS_GROUPING; name: string} | undefined>
    ) {
      state.selectedGrouping = action.payload;
    },
  },

  extraReducers: builder => {
    builder.addCase(AuthActions.logout.type, () => initialState);
  },
});

export const OffersActions = offerSlice.actions;

const OffersReducer = offerSlice.reducer;
export default OffersReducer;
